import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from "react-redux";
import './OdoMeter.css';
import CustomOdometer from './CustomOdometer';
import { HubConnectionBuilder } from '@microsoft/signalr';
import { setLoginError, logout } from "../redux/authSlice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

const OdoMeter = ({ onLogout }) => {
  const [todaysMissed, setTodaysMissed] = useState(0); 
  const [yesterdaysMissed, setYesterdaysMissed] = useState(0); 
  const connectionRef = useRef(null); // UseRef to avoid unnecessary state updates
  const accessToken = useSelector((state) => state.auth.accessToken);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInitialCounts = async () => {
      if (!accessToken) {
        console.warn("Access token not available. Skipping fetch.");
        dispatch(logout());
        navigate("/");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}missedcalllogs`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.status === 401) {
          console.warn("Unauthorized token. Logging out...");
          dispatch(setLoginError("Session expired. Please log in again."));
          dispatch(logout());
          navigate("/");
          return;
        }

        if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);

        const data = await response.json();
        console.log("API Response:", data);
        setTodaysMissed(data.todayCount || 0);
        setYesterdaysMissed(data.yesterdayCount || 0);
      } catch (err) {
        console.error("Error fetching initial missed call counts:", err);
        dispatch(logout());
        navigate("/");
        toast.error("Network error occurred. Please try again.", { style: { background: "red", color: "white" } });
      }
    };

    fetchInitialCounts();
  }, [accessToken, dispatch, navigate]); // Removed `connection` from dependencies

  useEffect(() => {
    const setupSignalRConnection = async () => {
      if (!accessToken || connectionRef.current) return;

      try {
        const newConnection = new HubConnectionBuilder()
          .withUrl(`${process.env.REACT_APP_BASE_URL_SIGNALR}missedcallcounthub`, {
            accessTokenFactory: () => accessToken,
          })
          .withAutomaticReconnect()
          .build();

        await newConnection.start();
        console.log("Connected to SignalR hub");
        connectionRef.current = newConnection;

        newConnection.on("MissedCallMetricsCount", (count) => {
          if (typeof count === "number") {
            console.log("Missed call count received:", count);
            setTodaysMissed(count);
          } else {
            console.error("Invalid data type for received missed call count:", count);
          }
        });
      } catch (err) {
        console.error("Error setting up SignalR connection:", err);
      }
    };

    setupSignalRConnection();

    return () => {
      if (connectionRef.current) {
        connectionRef.current
          .stop()
          .then(() => console.log('SignalR connection stopped'))
          .catch((err) => console.error('Error stopping SignalR connection:', err));
        connectionRef.current = null;
      }
    };
  }, [accessToken]); // Ensure SignalR connection only initializes once

  return (
    <div className="odo-wrapper">
      <div className='card'>
        <p className='card-title'>Today's Missed Calls</p>
        <CustomOdometer value={todaysMissed} type="todays" />
      </div>
      <div className='card'>
        <p className='card-title'>Yesterday's Missed Calls</p>
        <CustomOdometer value={yesterdaysMissed} type="yesterdays" />
      </div>
    </div>
  );
};

export default OdoMeter;
